import React, { useState } from "react";

import useDeleteAccount from "@fartherfinance/frontend/api/Accounts/hooks/useDeleteAccount";
import { ClientId, FartherAccountId } from "@fartherfinance/frontend/api/Types";

import LoadingModal from "../Shared/LoadingModal";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import SimpleModalWithCenteredButtons from "@src/sharedComponents/Modal/SimpleModalWithCenteredButtons/SimpleModalWithCenteredButtons";
import { handleApiError } from "@src/utils/handleApiError";

interface ArchiveFartherManagedAccountModalProps {
  isOpen?: boolean;
  clientId: ClientId;
  accountId: FartherAccountId;
  accountDisplayName: string;
  clientDisplayName: string;
  onClose: () => void;
}
const ArchiveFartherManagedAccountModal: React.FC<
  ArchiveFartherManagedAccountModalProps
> = ({
  isOpen = true,
  accountId,
  accountDisplayName,
  clientId,
  clientDisplayName,
  onClose,
}) => {
  const [isMutating, setIsMutating] = useState<boolean>(false);
  const statusNotification = useStatusNotification();

  const auth = useAdvisorRequestAuth();
  const softDeleteAccount = useDeleteAccount(auth);

  const onArchive = async () => {
    if (!accountId) {
      statusNotification("Error finding the account", "Error");
      return;
    }

    try {
      setIsMutating(true);

      await softDeleteAccount({
        clientId: clientId,
        accountId: accountId,
        deleteAccountAction: "Soft",
      });

      // Track account archival
      trackEvent({
        name: "Advisor Archive Account",
        properties: {
          accountId,
          clientId,
          accountDisplayName,
        },
      });

      setIsMutating(false);

      statusNotification("Account archived", "Success");

      onClose();
    } catch (error) {
      handleApiError({
        error: error,
        errorMessage: "Error archiving account:",
        genericErrorMessage: "Error archiving account",
        notify: statusNotification,
      });
      setIsMutating(false);
    }
  };

  if (isMutating || accountDisplayName === undefined) {
    return (
      <LoadingModal
        title={"Archive Account"}
        text="Removing the account will hide it from the client portal, but all records will remain preserved in our system."
        onClose={onClose}
      />
    );
  }

  return (
    <SimpleModalWithCenteredButtons
      title="Archive Account"
      text={`Removing the account "${accountDisplayName}" for ${clientDisplayName} will hide it from the client portal, but all records will remain preserved in our system.`}
      secondaryButton={{
        text: "Cancel",
        onClick: onClose,
      }}
      primaryButton={{
        text: "Archive",
        onClick: onArchive,
      }}
      onClose={onClose}
      isOpen={isOpen}
    />
  );
};

export default ArchiveFartherManagedAccountModal;
